import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { warrantyApi, uploadsApi } from '../../services/api';

interface OrderReadyForWarranty {
  id: string;
  orderNumber: string;
  sidemark: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerState: string;
  customerZip: string;
  customerCity: string;
  customerAddress: string;
  poNumber: string;
  installedAt: string;
  itemCount: number;
  productType: string;
  fabricType: string;
}

interface WarrantyRegistration {
  installerName: string;
  installerCompany: string;
  notes: string;
}

export const RegisterWarrantyPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ordersReady, setOrdersReady] = useState<OrderReadyForWarranty[]>([]);
  const [recentlyCompleted, setRecentlyCompleted] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState<OrderReadyForWarranty | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState<WarrantyRegistration>({
    installerName: '',
    installerCompany: '',
    notes: '',
  });

  useEffect(() => {
    loadOrders();
    loadRecentlyCompleted();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await warrantyApi.getOrdersReadyForRegistration();
      setOrdersReady(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentlyCompleted = async () => {
    try {
      const result = await warrantyApi.listRegistrations({ pageSize: 5 });
      setRecentlyCompleted(result.data || []);
    } catch (err) {
      console.error('Failed to load recently completed warranties:', err);
    }
  };

  const openModal = (order: OrderReadyForWarranty) => {
    setSelectedOrder(order);
    setPhotoUrls([]);
    setFormData({
      installerName: '',
      installerCompany: '',
      notes: '',
    });
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setPhotoUrls([]);
    setFormData({
      installerName: '',
      installerCompany: '',
      notes: '',
    });
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingPhoto(true);
    setError(null);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const result = await uploadsApi.uploadWarrantyPhoto(file);
        return result.url;
      });

      const urls = await Promise.all(uploadPromises);
      setPhotoUrls((prev) => [...prev, ...urls].slice(0, 4)); // Max 4 photos
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (index: number) => {
    setPhotoUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedOrder) return;

    // Validation
    if (!formData.installerName.trim()) {
      setError('Installer Name is required');
      return;
    }
    if (!formData.installerCompany.trim()) {
      setError('Installer Company is required');
      return;
    }
    if (photoUrls.length < 2) {
      setError('Please upload at least 2 installation photos');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await warrantyApi.createRegistration({
        orderId: selectedOrder.id,
        orderNumber: selectedOrder.orderNumber,
        customerName: selectedOrder.customerName,
        customerEmail: selectedOrder.customerEmail,
        customerPhone: selectedOrder.customerPhone,
        customerAddress: selectedOrder.customerAddress,
        customerCity: selectedOrder.customerCity,
        customerState: selectedOrder.customerState,
        customerZip: selectedOrder.customerZip,
        installDate: selectedOrder.installedAt,
        installerName: formData.installerName,
        installerCompany: formData.installerCompany,
        photoUrls,
        notes: formData.notes,
      });

      // Success! Reload orders and close modal
      closeModal();
      loadOrders();
      loadRecentlyCompleted();
      alert('Warranty registered successfully! Confirmation email sent to customer.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to register warranty');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Register Warranty</h1>
        <p className="text-gray-400 mt-1">
          Complete warranty registration for installed orders
        </p>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Orders Ready for Registration */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Orders Ready for Warranty Registration</h2>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-keder-yellow mx-auto"></div>
            <p className="text-gray-400 mt-2">Loading orders...</p>
          </div>
        ) : ordersReady.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg font-medium">No orders ready for warranty registration</p>
            <p className="text-sm mt-1">Orders will appear here once they are marked as installed</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-keder-gray">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Order #</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Install Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Product</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Units</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Location</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody>
                {ordersReady.map((order) => (
                  <tr key={order.id} className="border-b border-keder-gray/30 hover:bg-keder-gray/20">
                    <td className="px-4 py-3 text-white font-medium">{order.orderNumber}</td>
                    <td className="px-4 py-3">
                      <div className="text-white">{order.customerName}</div>
                      <div className="text-sm text-gray-400">{order.sidemark}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {order.installedAt ? new Date(order.installedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{order.productType || '-'}</div>
                      <div className="text-sm text-gray-400">{order.fabricType || '-'}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">{order.itemCount}</td>
                    <td className="px-4 py-3 text-gray-300">
                      {order.customerCity}, {order.customerState} {order.customerZip}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openModal(order)}
                        className="btn-primary text-sm"
                      >
                        Register Warranty
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recently Completed Warranties */}
      {recentlyCompleted.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Recently Completed Warranties</h2>
          <div className="space-y-2">
            {recentlyCompleted.map((warranty) => (
              <div key={warranty.id} className="flex justify-between items-center p-3 bg-keder-gray/20 rounded-lg">
                <div>
                  <p className="text-white font-medium">{warranty.orderNumber}</p>
                  <p className="text-sm text-gray-400">{warranty.customerName}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-green-400">Registered</p>
                  <p className="text-xs text-gray-400">
                    {new Date(warranty.registeredAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warranty Completion Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-keder-black border border-keder-gray rounded-lg max-w-3xl w-full my-8">
            <div className="p-6 border-b border-keder-gray">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-white">Complete Warranty Registration</h2>
                  <p className="text-gray-400 mt-1">Order: {selectedOrder.orderNumber}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Order Info (Pre-populated, Read-only) */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-3">ORDER INFORMATION (Auto-filled)</h3>
                <div className="grid grid-cols-2 gap-4 p-4 bg-keder-gray/20 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-400">Customer Name</p>
                    <p className="text-white">{selectedOrder.customerName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Install Date</p>
                    <p className="text-white">
                      {selectedOrder.installedAt ? new Date(selectedOrder.installedAt).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Phone</p>
                    <p className="text-white">{selectedOrder.customerPhone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Email</p>
                    <p className="text-white">{selectedOrder.customerEmail || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-400">Location</p>
                    <p className="text-white">
                      {selectedOrder.customerCity}, {selectedOrder.customerState} {selectedOrder.customerZip}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Product Type</p>
                    <p className="text-white">{selectedOrder.productType || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400"># of Screens</p>
                    <p className="text-white">{selectedOrder.itemCount}</p>
                  </div>
                </div>
              </div>

              {/* Installer Information */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-3">INSTALLER INFORMATION *</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Installer Name *</label>
                    <input
                      type="text"
                      value={formData.installerName}
                      onChange={(e) => setFormData({ ...formData, installerName: e.target.value })}
                      className="input-field w-full"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Installer Company *</label>
                    <input
                      type="text"
                      value={formData.installerCompany}
                      onChange={(e) => setFormData({ ...formData, installerCompany: e.target.value })}
                      className="input-field w-full"
                      placeholder="ABC Installation Co."
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Installation Photos */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-1">INSTALLATION PHOTOS *</h3>
                <p className="text-xs text-gray-400 mb-3">
                  Upload 2-4 photos: housing mounted, tracks installed, full unit view, motor location
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {photoUrls.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}

                  {photoUrls.length < 4 && (
                    <label className="border-2 border-dashed border-keder-gray rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-keder-yellow transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                      {uploadingPhoto ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-keder-yellow" />
                      ) : (
                        <>
                          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-gray-400 text-sm mt-1">Add Photo</span>
                        </>
                      )}
                    </label>
                  )}
                </div>

                {photoUrls.length < 2 && (
                  <p className="text-amber-400 text-sm mt-2">* Minimum 2 photos required</p>
                )}
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-3">ADDITIONAL NOTES</h3>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="input-field w-full"
                  placeholder="Any additional information about the installation..."
                />
              </div>
            </div>

            <div className="px-6 py-3 border-t border-keder-gray">
              <p className="text-xs text-gray-500 text-center">
                Warranty requires proper installation per manufacturer guidelines.
              </p>
            </div>

            <div className="p-6 border-t border-keder-gray flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="btn-secondary"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || photoUrls.length < 2}
                className="btn-primary"
              >
                {submitting ? 'Submitting...' : 'Complete Registration'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterWarrantyPage;
